/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ArrowRight, Download, GitCompare, Calendar, Building2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter32 P4 — Admin Commercial Ownership Mismatches.
 *
 * Lists sales where the SF view of ownership (sf_agent_ref_id) differs from the
 * internal commercial owner (commercial_agent_id). Strong signal for attribution
 * disputes between PPC ingestion and AQS internal commission allocation.
 */
export default function AdminCommercialMismatches() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 50;
  
  const [filters, setFilters] = useState({
    agency_id: "",
    signed_from: "",
    signed_to: "",
  });
  const [agencies, setAgencies] = useState([]);

  const fetchData = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(limit) });
      if (filters.agency_id) params.set("agency_id", filters.agency_id);
      if (filters.signed_from) params.set("signed_from", filters.signed_from);
      if (filters.signed_to) params.set("signed_to", filters.signed_to);
      const r = await axios.get(`${API}/api/admin/commercial-ownership-mismatches?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPage(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcarea raportului.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/agencies?per_page=200`);
        setAgencies(r.data?.data || r.data?.items || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams({ export_csv: "true" });
      if (filters.agency_id) params.set("agency_id", filters.agency_id);
      if (filters.signed_from) params.set("signed_from", filters.signed_from);
      if (filters.signed_to) params.set("signed_to", filters.signed_to);
      const r = await axios.get(
        `${API}/api/admin/commercial-ownership-mismatches?${params.toString()}`,
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commercial-ownership-mismatches-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportat.");
    } catch (e) {
      toast.error("Eroare la export.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-commercial-mismatches-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/admin/gdpr-compliance" className="hover:text-[#1e3a8a]">Administrare</Link>
            <ArrowRight className="h-3 w-3" />
            <span>Mismatch Ownership</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Discrepanțe Owner Comercial
          </h1>
          <p className="mt-1 text-slate-500">
            Vânzări unde owner-ul SF și owner-ul comercial intern sunt diferiți.
            Semnal pentru dispute de attribution la comisionare.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(1)}
            data-testid="refresh-mismatches"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualizează
          </Button>
          <Button
            onClick={downloadCsv}
            size="sm"
            className="bg-[#1e3a8a] hover:bg-[#1e40af]"
            data-testid="export-mismatches-csv"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <GitCompare className="h-4 w-4 text-[#1e3a8a]" />
            Filtre
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                <Building2 className="mr-1 inline h-3 w-3" />
                Agenție
              </Label>
              <select
                value={filters.agency_id}
                onChange={(e) => setFilters({ ...filters, agency_id: e.target.value })}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid="filter-agency"
              >
                <option value="">— Toate —</option>
                {agencies.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                <Calendar className="mr-1 inline h-3 w-3" />
                Semnat de la
              </Label>
              <Input
                type="date"
                value={filters.signed_from}
                onChange={(e) => setFilters({ ...filters, signed_from: e.target.value })}
                className="mt-1"
                data-testid="filter-signed-from"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                <Calendar className="mr-1 inline h-3 w-3" />
                Semnat până la
              </Label>
              <Input
                type="date"
                value={filters.signed_to}
                onChange={(e) => setFilters({ ...filters, signed_to: e.target.value })}
                className="mt-1"
                data-testid="filter-signed-to"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => fetchData(1)}
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="apply-filters"
              >
                Aplică filtre
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">Rezultate</CardTitle>
            <CardDescription data-testid="result-count">
              {loading ? "Se încarcă…" : `${total} discrepanțe găsite`}
            </CardDescription>
          </div>
          {total > 0 && (
            <div className="flex items-center gap-1 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
              <AlertTriangle className="h-3 w-3" />
              Verifică alocarea pentru aceste vânzări
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Vânzare</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Client</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Contract</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">SF Owner</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Owner Comercial</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Semnat</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Ultim override</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                      Se încarcă…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-slate-400 italic" data-testid="empty-state">
                      Nicio discrepanță. Toate vânzările au owner SF și comercial aliniați.
                    </td>
                  </tr>
                )}
                {items.map((it) => (
                  <tr
                    key={it.sale_id}
                    className="hover:bg-slate-50/50"
                    data-testid={`mismatch-row-${it.sale_id}`}
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/sales/${it.sale_id}`}
                        className="font-mono text-xs text-[#1e3a8a] hover:underline"
                      >
                        {it.sale_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-slate-800">
                        {it.client_name || "—"}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {it.client_id_doc || ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium text-slate-700">{it.contract_number || "—"}</div>
                      <div className="text-slate-500">POD {it.pod || "—"}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-800">
                        {it.sf_agent_name || "—"}
                      </Badge>
                      {it.sf_agent_code && (
                        <div className="mt-0.5 text-[10px] text-slate-400">{it.sf_agent_code}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-800">
                        {it.commercial_agent_name || "—"}
                      </Badge>
                      {it.commercial_agent_code && (
                        <div className="mt-0.5 text-[10px] text-slate-400">{it.commercial_agent_code}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {it.signed_date || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {it.last_override ? (
                        <div>
                          <div className="text-slate-600">
                            {new Date(it.last_override.timestamp).toLocaleString("ro-RO")}
                          </div>
                          {it.last_override.reason && (
                            <div className="mt-0.5 italic text-slate-500 line-clamp-1" title={it.last_override.reason}>
                              „{it.last_override.reason}"
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="italic text-slate-400">fără override</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-slate-500">
                Pagina {page} / {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  ‹ Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page + 1)}
                  disabled={page >= totalPages || loading}
                >
                  Următor ›
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
